import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { formatDistanceToNow, format } from 'date-fns';
import {
  ArrowLeft, AlertCircle, Loader, Gavel, Calendar, Globe, Server, Shield,
  UserPlus, FileText, AlertTriangle, XCircle, RefreshCw, Clock, Eye,
  EyeOff, Mail, Wifi, Gamepad2, Users, MessageSquare, ChevronDown,
} from 'lucide-react';

function safeDate(ts: number | null | undefined): Date | null {
  if (ts == null || isNaN(ts)) return null;
  const ms = ts < 1e12 ? ts * 1000 : ts;
  const d = new Date(ms);
  return isNaN(d.getTime()) ? null : d;
}

function safeFmtDistance(ts: number | null | undefined): string {
  const d = safeDate(ts);
  return d ? formatDistanceToNow(d, { addSuffix: true }) : '—';
}

function safeFmt(ts: number | null | undefined, fmt = 'PPP p'): string {
  const d = safeDate(ts);
  return d ? format(d, fmt) : '—';
}
import { cn } from '../utils/cn';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useAuth } from '../contexts/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { appealsApiService } from '../services/appealsApi';
import { AppealDetail, PLAYER_QUESTIONS, APPEAL_TERMINAL_STATUSES, StaffAnswersRequest, STAFF_QUESTIONS } from '../types/appeals';
import { AppealStatusBadge } from '../components/appeals/AppealStatusBadge';
import { AppealVersionBadge } from '../components/appeals/AppealVersionBadge';
import { AppealWorkflowBanner } from '../components/appeals/AppealWorkflowBanner';
import { JuryVotesSection } from '../components/appeals/JuryVotesSection';
import { V2JuryStageVotesSection } from '../components/appeals/V2JuryStageVotesSection';
import { V2OutcomeBanner } from '../components/appeals/V2OutcomeBanner';
import { isV2Appeal } from '../utils/appealsV2';
import { AppealHistorySection } from '../components/appeals/AppealHistorySection';
import { AssignStaffModal } from '../components/appeals/AssignStaffModal';
import { StaffAnswersModal } from '../components/appeals/StaffAnswersModal';
import { EscalateModal } from '../components/appeals/EscalateModal';
import { PlayerProfile } from '../components/PlayerProfile';
import { PlayerModerationContext } from '../components/appeals/PlayerModerationContext';
import { EvidenceSection } from '../components/EvidenceSection';
import { LogbookCaseSection } from '../components/logbook/LogbookCaseSection';
import { TicketLinks } from '../components/TicketLinks';
import { usernameCacheService } from '../services/usernameCache';
import { decodeHtmlEntities } from '../utils/decodeHtmlEntities';
import { KanbanLinkedCardsSection } from '../components/kanban/KanbanLinkedCardsSection';
import { PageHeader } from '../components/ui/PageHeader';
import { SectionHeader } from '../components/ui/SectionHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonLoader } from '../components/SkeletonLoader';

export function AppealDetailView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { identity } = useAuth();
  const isMobile = useIsMobile();
  const isDev = import.meta.env.DEV;

  const [appeal, setAppeal] = useState<AppealDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isReassignModalOpen, setIsReassignModalOpen] = useState(false);
  const [isStaffAnswersModalOpen, setIsStaffAnswersModalOpen] = useState(false);
  const [isEscalateModalOpen, setIsEscalateModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [showPlayerAnswers, setShowPlayerAnswers] = useState(true);
  const [showStaffAnswers, setShowStaffAnswers] = useState(true);
  const [showSensitive, setShowSensitive] = useState(false);

  useDocumentTitle(appeal ? `Appeal #${appeal.id}` : 'Appeal Details');

  const canManage = useMemo(() => {
    if (isDev) return true;
    if (!identity) return false;
    const groups = identity.raw.custom.groups;
    return groups.includes('SPY_Bans_Admin') || groups.includes('SPY_Bans_Superadmins') || groups.includes('SPY_Super_admin');
  }, [identity, isDev]);

  const isAdmin = useMemo(() => {
    if (isDev) return true;
    if (!identity) return false;
    const groups = identity.raw.custom.groups;
    return groups.includes('SPY_Bans_Superadmins') || groups.includes('SPY_Super_admin');
  }, [identity, isDev]);

  const isHelper = useMemo(() => {
    if (!identity) return false;
    const groups = identity.raw.custom.groups;
    return groups.includes('SPY_Bans_Helper');
  }, [identity]);

  const isAssignedToMe = useMemo(() => {
    if (!identity || !appeal) return false;
    const myUuid = identity.raw.custom.minecraft_uuid;
    return myUuid != null && myUuid === appeal.assigned_staff_id;
  }, [identity, appeal]);

  const canAnswerQuestions = canManage || (isHelper && isAssignedToMe);

  const isTerminal = appeal ? APPEAL_TERMINAL_STATUSES.includes(appeal.status) : false;

  const loadAppeal = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await appealsApiService.getAppealDetail(parseInt(id, 10));
      setAppeal(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load appeal');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadAppeal();
  }, [loadAppeal]);

  useEffect(() => {
    if (!appeal) return;
    const uuids = [
      appeal.user_uuid,
      appeal.assigned_staff_id,
      appeal.ban_issued_by,
      ...(appeal.jury_votes || []).map(v => v.juror_minecraft_uuid).filter(Boolean),
      ...(appeal.jury_stage_votes?.stage1 || []).map(v => v.juror_minecraft_uuid).filter(Boolean),
      ...(appeal.jury_stage_votes?.stage2 || []).map(v => v.juror_minecraft_uuid).filter(Boolean),
    ].filter(Boolean) as string[];
    usernameCacheService.prefetchUsernames(uuids);
  }, [appeal]);

  const handleAssign = async (staffUuid: string) => {
    if (!appeal) return;
    await appealsApiService.assignStaff(appeal.id, staffUuid);
    await loadAppeal();
  };

  const handleReassign = async (staffUuid: string) => {
    if (!appeal) return;
    await appealsApiService.reassignStaff(appeal.id, staffUuid);
    await loadAppeal();
  };

  const handleSubmitAnswers = async (answers: StaffAnswersRequest) => {
    if (!appeal) return;
    await appealsApiService.submitStaffAnswers(appeal.id, answers);
    await loadAppeal();
  };

  const handleEscalate = async (notes: string) => {
    if (!appeal) return;
    await appealsApiService.escalateAppeal(appeal.id, notes);
    await loadAppeal();
  };

  const handleCancel = async (notes: string) => {
    if (!appeal) return;
    await appealsApiService.cancelAppeal(appeal.id, notes);
    await loadAppeal();
  };

  if (loading) {
    return (
      <div className="space-y-6" aria-busy="true">
        <PageHeader
          eyebrow="Moderation"
          onBack={() => navigate('/appeals')}
          backLabel="Appeals"
          title="Appeal"
          description="Loading appeal…"
          meta={<Loader className="w-3.5 h-3.5 animate-spin text-glass-400" aria-label="Loading appeal" />}
        />
        <div className={cn('flex flex-col gap-6', !isMobile && 'lg:flex-row')}>
          <div className="flex-1 space-y-6 min-w-0">
            {[0, 1].map((i) => (
              <div key={i} className="glass-card p-4 md:p-5 space-y-3">
                <SkeletonLoader className="h-4 w-40" />
                <SkeletonLoader className="h-3 w-full" />
                <SkeletonLoader className="h-3 w-5/6" />
                <SkeletonLoader className="h-3 w-2/3" />
              </div>
            ))}
          </div>
          <div className={cn('space-y-4', !isMobile && 'lg:w-80 lg:flex-shrink-0')}>
            {[0, 1, 2].map((i) => (
              <div key={i} className="glass-card p-4 space-y-3">
                <SkeletonLoader className="h-4 w-28" />
                <SkeletonLoader className="h-3 w-full" />
                <SkeletonLoader className="h-3 w-3/4" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !appeal) {
    return (
      <div className="space-y-6">
        <PageHeader
          eyebrow="Moderation"
          onBack={() => navigate('/appeals')}
          backLabel="Appeals"
          title="Appeal"
        />
        <EmptyState
          tone="danger"
          icon={AlertCircle}
          title="Appeal not found"
          description={error || 'The requested appeal could not be found.'}
          action={
            <>
              <button onClick={loadAppeal} className="glass-button">Retry</button>
              <button onClick={() => navigate('/appeals')} className="glass-button-secondary">
                <ArrowLeft className="w-4 h-4" />
                Back to Appeals
              </button>
            </>
          }
        />
      </div>
    );
  }

  const isPermanentBan = appeal.ban_expires === null;
  const isBanActive = appeal.ban_enforced === 1;
  const isV2 = isV2Appeal(appeal);

  return (
    <div className="space-y-6">
      {/* Header + actions */}
      <PageHeader
        eyebrow="Moderation"
        onBack={() => navigate('/appeals')}
        backLabel="Appeals"
        title={<span className="tabular-nums">Appeal #{appeal.id}</span>}
        titleAdornment={
          <>
            <AppealStatusBadge status={appeal.status} />
            <AppealVersionBadge appeal={appeal} />
          </>
        }
        description={
          <span className="tabular-nums">
            Submitted {safeFmtDistance(appeal.created_at)}
            {' — '}
            {safeFmt(appeal.created_at)}
          </span>
        }
        actions={
          <>
            {/* Staff actions */}
            {canManage && !isTerminal && (
              <>
                {appeal.status === 'pending' && (
                  <button onClick={() => setIsAssignModalOpen(true)} className="btn-primary">
                    <UserPlus className="w-4 h-4" /> Assign Staff
                  </button>
                )}
                {appeal.status === 'awaiting_staff' && (
                  <>
                    <button onClick={() => setIsReassignModalOpen(true)} className="glass-button">
                      <UserPlus className="w-4 h-4" /> Reassign
                    </button>
                    <button onClick={() => setIsStaffAnswersModalOpen(true)} className="btn-primary">
                      <FileText className="w-4 h-4" /> Submit Answers
                    </button>
                  </>
                )}
                <button onClick={() => setIsEscalateModalOpen(true)} className="glass-button text-amber-300 hover:text-amber-200">
                  <AlertTriangle className="w-4 h-4" /> Escalate
                </button>
                <button onClick={() => setIsCancelModalOpen(true)} className="btn-danger">
                  <XCircle className="w-4 h-4" /> Cancel
                </button>
              </>
            )}

            {/* Helper actions — only Submit Answers when assigned */}
            {!canManage && canAnswerQuestions && !isTerminal && appeal.status === 'awaiting_staff' && (
              <button onClick={() => setIsStaffAnswersModalOpen(true)} className="btn-primary">
                <FileText className="w-4 h-4" /> Submit Answers
              </button>
            )}

            <button onClick={loadAppeal} className="btn-icon" title="Refresh" aria-label="Refresh appeal">
              <RefreshCw className="w-4 h-4" />
            </button>
          </>
        }
      />

      {/* Workflow phase indicator */}
      <AppealWorkflowBanner
        status={appeal.status}
        appealVersion={isV2 ? 2 : 1}
        v2JuryStage={appeal.v2_jury_stage}
      />

      {/* Jury V2 outcome banner (renders only for decided V2 appeals) */}
      <V2OutcomeBanner appeal={appeal} />

      {/* Verdict banner (V1 only — V2 appeals never have a verdict) */}
      {appeal.verdict && (
        <div className={cn(
          'alert flex-col items-stretch gap-2',
          appeal.verdict === 'overturned' ? 'alert-success' : 'alert-danger'
        )} role="status">
          <div className="flex items-center gap-2">
            <Gavel className={cn('w-4 h-4', appeal.verdict === 'overturned' ? 'text-green-400' : 'text-red-400')} />
            <span className="font-semibold text-white">
              Verdict: <span className={appeal.verdict === 'overturned' ? 'text-green-400' : 'text-red-400'}>
                {appeal.verdict === 'overturned' ? 'Ban Overturned' : 'Ban Upheld'}
              </span>
            </span>
          </div>
          {appeal.decision_reason && (
            <p className="text-sm text-glass-300 ml-6 whitespace-pre-wrap">{decodeHtmlEntities(appeal.decision_reason)}</p>
          )}
          {appeal.decided_at && (
            <p className="text-xs text-glass-400 ml-6 tabular-nums">
              Decided {safeFmtDistance(appeal.decided_at)}
            </p>
          )}
        </div>
      )}

      {/* Reviewer notes (escalated/cancelled) */}
      {appeal.reviewer_notes && (
        <div className="alert alert-warning flex-col items-stretch gap-1" role="note">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-amber-400" />
            <span className="font-semibold text-white text-sm">Reviewer Notes</span>
          </div>
          <p className="text-sm text-glass-300 ml-6 whitespace-pre-wrap">{decodeHtmlEntities(appeal.reviewer_notes)}</p>
        </div>
      )}

      <div className={cn('flex flex-col gap-6', !isMobile && 'lg:flex-row')}>
        {/* Main content */}
        <div className="flex-1 space-y-6 min-w-0">

          {/* Player's Answers */}
          <div className="glass-card p-4 md:p-5">
            <button
              onClick={() => setShowPlayerAnswers(!showPlayerAnswers)}
              className="flex items-center justify-between gap-3 w-full text-left rounded"
              aria-expanded={showPlayerAnswers}
            >
              <div className="flex items-center gap-2 min-w-0">
                <MessageSquare className="w-4 h-4 text-blue-400 shrink-0" />
                <h3 className="text-sm font-semibold text-white">Player's Answers</h3>
                <span className="badge badge-neutral">9 questions</span>
              </div>
              <ChevronDown className={cn('w-4 h-4 text-glass-400 transition-transform', showPlayerAnswers && 'rotate-180')} />
            </button>
            {showPlayerAnswers && (
              <div className="mt-4 divide-y divide-glass-200/10">
                {PLAYER_QUESTIONS.map(q => {
                  const value = appeal[q.field as keyof AppealDetail] as string | null;
                  if (!value) return null;
                  return (
                    <div key={q.field} className="py-3 first:pt-0 last:pb-0">
                      <p className="eyebrow text-accent-300 mb-1">{q.label}</p>
                      <p className="text-sm text-glass-300 whitespace-pre-wrap">{decodeHtmlEntities(value)}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Staff Answers */}
          {(appeal.sq_rules_broken || appeal.status === 'awaiting_staff') && (
            <div className="glass-card p-4 md:p-5">
              <button
                onClick={() => setShowStaffAnswers(!showStaffAnswers)}
                className="flex items-center justify-between gap-3 w-full text-left rounded"
                aria-expanded={showStaffAnswers}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="w-4 h-4 text-orange-400 shrink-0" />
                  <h3 className="text-sm font-semibold text-white">Staff Answers</h3>
                  {appeal.sq_rules_broken ? (
                    <span className="badge badge-dot badge-success">Completed</span>
                  ) : (
                    <span className="badge badge-dot badge-warning">Pending</span>
                  )}
                </div>
                <ChevronDown className={cn('w-4 h-4 text-glass-400 transition-transform', showStaffAnswers && 'rotate-180')} />
              </button>
              {showStaffAnswers && (
                <div className="mt-4">
                  {appeal.sq_rules_broken ? (
                    <div className="divide-y divide-glass-200/10">
                      {STAFF_QUESTIONS.map(q => {
                        const value = appeal[q.field as keyof AppealDetail] as string | null;
                        return (
                          <div key={q.field} className="py-3 first:pt-0 last:pb-0">
                            <p className="eyebrow text-orange-300 mb-1">{q.label}</p>
                            <p className="text-sm text-glass-300 whitespace-pre-wrap">{value ? decodeHtmlEntities(value) : '—'}</p>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <EmptyState
                      bare
                      compact
                      icon={FileText}
                      title="Staff answers have not been submitted yet."
                      action={canAnswerQuestions && appeal.status === 'awaiting_staff' ? (
                        <button
                          onClick={() => setIsStaffAnswersModalOpen(true)}
                          className="glass-button"
                        >
                          <FileText className="w-4 h-4" />
                          Submit Answers Now
                        </button>
                      ) : undefined}
                    />
                  )}
                </div>
              )}
            </div>
          )}

          {appeal.jury_exclusion_check && (
            <div className="glass-card p-4 md:p-5 space-y-2">
              <p className="eyebrow text-glass-300">Automatic jury exclusions</p>
              <p className="text-sm text-glass-300" role="status">
                {appeal.jury_exclusion_check.status === 'unavailable'
                  ? 'IdentSure screening is waiting to retry. New jurors cannot be selected until a complete check succeeds.'
                  : appeal.jury_exclusion_check.status === 'checked'
                    ? `${appeal.jury_exclusion_check.automatic_count} linked Discord account(s) excluded at the last check.`
                    : 'IdentSure will check for conflicts before jurors are selected.'}
              </p>
              <p className="text-xs text-glass-500">
                Checks cover the appellant and members of their likely circles and observed teams, alongside any additional exclusions. Relationship evidence is not included in the jury’s answers.
              </p>
            </div>
          )}

          {/* Jury Votes — dispatch by appeal version (V1 rendering untouched) */}
          {isV2 ? (
            <V2JuryStageVotesSection
              appealId={appeal.id}
              appealStatus={appeal.status}
              juryStage={appeal.v2_jury_stage}
              stage1Result={appeal.v2_stage1_result}
              stage2Result={appeal.v2_stage2_result}
              initialVotes={appeal.jury_stage_votes}
            />
          ) : (
            <JuryVotesSection votes={appeal.jury_votes || []} appealStatus={appeal.status} />
          )}

          {/* Appeal History */}
          <AppealHistorySection history={appeal.appeal_history || []} currentAppealId={appeal.id} />

          {/* Player Moderation Context */}
          <PlayerModerationContext
            userUuid={appeal.user_uuid}
            currentBanId={appeal.ban_uuid}
          />

          {/* Evidence & Tickets for the ban */}
          <div className="glass-card p-4 md:p-5 space-y-4">
            <SectionHeader title="Ban Evidence & Tickets" icon={Eye} iconClassName="text-blue-400" className="mb-0" />
            <TicketLinks itemId={appeal.ban_uuid} itemType="ban" />
            <EvidenceSection itemId={appeal.ban_uuid} itemType="ban" />
            <LogbookCaseSection itemType="ban" itemId={appeal.ban_uuid} subjectUuid={appeal.user_uuid} />
          </div>

        </div>

        {/* Sidebar */}
        <div className={cn('space-y-4', !isMobile && 'lg:w-80 lg:flex-shrink-0')}>

          {/* Player Info */}
          <div className="glass-card p-4 space-y-3">
            <SectionHeader title="Appellant" icon={Users} iconClassName="text-green-400" className="mb-0" />
            <PlayerProfile uuid={appeal.user_uuid} name={appeal.username} showAvatar showUuid />
            {appeal.player_discord_id && (
              <div className="flex items-center gap-2 text-xs text-glass-400">
                <MessageSquare className="w-3.5 h-3.5" />
                <span>Discord: <span className="font-mono text-glass-300">{appeal.player_discord_id}</span></span>
              </div>
            )}
            {appeal.player_playtime !== null && (
              <div className="flex items-center gap-2 text-xs text-glass-400">
                <Gamepad2 className="w-3.5 h-3.5" />
                <span>Playtime: <span className="text-glass-300 tabular-nums">{Math.floor((appeal.player_playtime || 0) / 3600)}h</span></span>
              </div>
            )}
            {appeal.player_first_joined && (
              <div className="flex items-center gap-2 text-xs text-glass-400">
                <Calendar className="w-3.5 h-3.5" />
                <span>First joined: <span className="text-glass-300 tabular-nums">{safeFmtDistance(appeal.player_first_joined)}</span></span>
              </div>
            )}
            {appeal.player_last_joined && (
              <div className="flex items-center gap-2 text-xs text-glass-400">
                <Clock className="w-3.5 h-3.5" />
                <span>Last joined: <span className="text-glass-300 tabular-nums">{safeFmtDistance(appeal.player_last_joined)}</span></span>
              </div>
            )}
            {appeal.player_servers_seen !== null && (
              <div className="flex items-center gap-2 text-xs text-glass-400">
                <Server className="w-3.5 h-3.5" />
                <span>Servers seen: <span className="text-glass-300 tabular-nums">{appeal.player_servers_seen}</span></span>
              </div>
            )}

            {/* Sensitive fields */}
            {isAdmin && (
              <div className="border-t border-glass-200/10 pt-3 mt-3">
                <button
                  onClick={() => setShowSensitive(!showSensitive)}
                  className="glass-button-secondary btn-xs w-full"
                  aria-pressed={showSensitive}
                >
                  {showSensitive ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  {showSensitive ? 'Hide' : 'Show'} Sensitive Info
                </button>
                {showSensitive && (
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center gap-2 text-xs">
                      <Mail className="w-3.5 h-3.5 text-amber-400" />
                      <span className="text-glass-400">Email:</span>
                      <span className="font-mono text-amber-300 text-xs">{appeal.contact_email}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <Wifi className="w-3.5 h-3.5 text-amber-400" />
                      <span className="text-glass-400">IP:</span>
                      <span className="font-mono text-amber-300 text-xs">{appeal.ip_address}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="border-t border-glass-200/10 pt-3 mt-3 flex flex-wrap gap-2">
              <Link to={`/player/${appeal.user_uuid}`} className="glass-button-sm">
                View Profile
              </Link>
              <Link to={`/bans?highlight=${appeal.ban_uuid}`} className="glass-button-sm">
                View Bans
              </Link>
            </div>
          </div>

          {/* Ban Details */}
          <div className="glass-card p-4 space-y-3">
            <SectionHeader
              title="Ban Details"
              icon={Shield}
              iconClassName="text-red-400"
              className="mb-0"
              actions={<span className="text-xs font-mono tabular-nums text-glass-400">#{appeal.ban_id}</span>}
            />

            <p className="text-sm text-glass-300 subcard px-2.5 py-2 whitespace-pre-wrap">{decodeHtmlEntities(appeal.ban_reason)}</p>

            <dl className="space-y-2 text-xs">
              <div className="flex items-center justify-between gap-2">
                <dt className="text-glass-400">Status</dt>
                <dd>
                  <span className={cn('badge badge-dot', isBanActive ? 'badge-danger' : 'badge-neutral')}>
                    {isBanActive ? 'Active' : 'Not Enforced'}
                  </span>
                </dd>
              </div>
              <div className="flex items-center justify-between gap-2">
                <dt className="text-glass-400">Duration</dt>
                <dd className="text-glass-300 tabular-nums">
                  {isPermanentBan ? 'Permanent' : safeFmtDistance(appeal.ban_expires)}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-2">
                <dt className="text-glass-400">Scope</dt>
                <dd className="flex items-center gap-1 text-glass-300">
                  {appeal.ban_scope === 'global' || appeal.ban_scope === 'universal'
                    ? <Globe className="w-3.5 h-3.5" />
                    : <Server className="w-3.5 h-3.5" />}
                  <span className="capitalize">{appeal.ban_scope}</span>
                  {appeal.ban_scope_target && <span className="text-glass-400 font-mono">({appeal.ban_scope_target})</span>}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-2">
                <dt className="text-glass-400">Appealable</dt>
                <dd className={appeal.ban_appealable ? 'text-green-400' : 'text-red-400'}>
                  {appeal.ban_appealable ? 'Yes' : 'No'}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-2">
                <dt className="text-glass-400">Banned</dt>
                <dd className="text-glass-300 tabular-nums">
                  {safeFmtDistance(appeal.ban_created)}
                </dd>
              </div>
              {appeal.ban_issued_by && (
                <div className="flex items-center justify-between gap-2">
                  <dt className="text-glass-400">Issued by</dt>
                  <dd><PlayerProfile uuid={appeal.ban_issued_by} inline showUuid={false} /></dd>
                </div>
              )}
            </dl>
          </div>

          {/* Assignment Info */}
          <div className="glass-card p-4 space-y-3">
            <SectionHeader title="Assignment" icon={UserPlus} className="mb-0" />
            {appeal.assigned_staff_id ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-glass-400">Assigned to:</span>
                  <PlayerProfile uuid={appeal.assigned_staff_id} inline showUuid={false} />
                </div>
                {appeal.assigned_at && (
                  <p className="text-xs text-glass-400 tabular-nums">
                    Assigned {safeFmtDistance(appeal.assigned_at)}
                  </p>
                )}
                {appeal.staff_answered_at && (
                  <p className="text-xs text-glass-400 tabular-nums">
                    Answered {safeFmtDistance(appeal.staff_answered_at)}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-glass-400">No staff member assigned yet.</p>
            )}
          </div>

          {/* Timeline */}
          <div className="glass-card p-4 space-y-3">
            <SectionHeader title="Timeline" icon={Clock} iconClassName="text-glass-400" className="mb-0" />
            <dl className="space-y-2 text-xs tabular-nums">
              <div className="flex justify-between gap-2">
                <dt className="text-glass-400">Created</dt>
                <dd className="text-glass-300 text-right">{safeFmt(appeal.created_at)}</dd>
              </div>
              {appeal.assigned_at && (
                <div className="flex justify-between gap-2">
                  <dt className="text-glass-400">Assigned</dt>
                  <dd className="text-glass-300 text-right">{safeFmt(appeal.assigned_at)}</dd>
                </div>
              )}
              {appeal.staff_answered_at && (
                <div className="flex justify-between gap-2">
                  <dt className="text-glass-400">Staff Answered</dt>
                  <dd className="text-glass-300 text-right">{safeFmt(appeal.staff_answered_at)}</dd>
                </div>
              )}
              {appeal.decided_at && (
                <div className="flex justify-between gap-2">
                  <dt className="text-glass-400">Decided</dt>
                  <dd className="text-glass-300 text-right">{safeFmt(appeal.decided_at)}</dd>
                </div>
              )}
              {appeal.updated_at && (
                <div className="flex justify-between gap-2">
                  <dt className="text-glass-400">Last Updated</dt>
                  <dd className="text-glass-300 text-right">{safeFmt(appeal.updated_at)}</dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AssignStaffModal
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        onSubmit={handleAssign}
        title="Assign Staff Reviewer"
      />

      <AssignStaffModal
        isOpen={isReassignModalOpen}
        onClose={() => setIsReassignModalOpen(false)}
        onSubmit={handleReassign}
        title="Reassign Staff Reviewer"
        currentAssigned={appeal.assigned_staff_id}
      />

      <StaffAnswersModal
        isOpen={isStaffAnswersModalOpen}
        onClose={() => setIsStaffAnswersModalOpen(false)}
        onSubmit={handleSubmitAnswers}
        existingAnswers={appeal.sq_rules_broken ? {
          sq_rules_broken: appeal.sq_rules_broken || '',
          sq_evidence: appeal.sq_evidence || '',
          sq_prior_offenses: appeal.sq_prior_offenses || '',
          sq_severity: appeal.sq_severity || '',
          sq_community_harm: appeal.sq_community_harm || '',
          sq_warning_given: appeal.sq_warning_given || '',
          sq_user_response: appeal.sq_user_response || '',
          sq_staff_interpretation: appeal.sq_staff_interpretation || '',
          sq_duration_reasoning: appeal.sq_duration_reasoning || '',
          sq_jury_exclusions: appeal.sq_jury_exclusions || '',
        } : undefined}
      />

      <EscalateModal
        isOpen={isEscalateModalOpen}
        onClose={() => setIsEscalateModalOpen(false)}
        onSubmit={handleEscalate}
        action="escalate"
      />

      <EscalateModal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        onSubmit={handleCancel}
        action="cancel"
      />

      {/* Linked Kanban Cards */}
      {id && <KanbanLinkedCardsSection linkType="appeal" targetId={id} title="Linked Kanban Cards" />}
    </div>
  );
}
